import React, {useEffect, useState} from 'react'
import {useLocation} from 'react-router-dom'
import {useRouteMatch} from 'react-router-dom'
import {getAppOrigin} from '@salesforce/pwa-kit-react-sdk/utils/url'
import {UseFormReturn} from 'react-hook-form'
import {useSearchForAddressFields} from '../../hooks/forms/useSearhForAddressField'
import {BoxProps, Flex, Stack, Text} from '../../vanilla'
import {AddressDetails} from '../../types/loqate'
import {CheckIcon, CrossBoldIcon} from '../icons'
import {LoqateField} from '../loqate-field'
import {POSTCODE_MIN_LENGTH, POSTCODE_MAX_LENGTH} from '../../utils/constants/validation'

interface SearchForAddressFieldProps {
  form: UseFormReturn<any>
  setEnterManually: (manually: boolean) => void
  isEnterManually: boolean
  inputGroupProps?: BoxProps<'div'>
  label?: string
  showAvailability?: boolean
  showAsterisk?: boolean
}

/* Reusable component for Address Fields on Register page, My Addresses, Delivery page  */
export const SearchForAddressField = (props: SearchForAddressFieldProps) => {
  const {
    form,
    setEnterManually,
    isEnterManually,
    inputGroupProps,
    label,
    showAvailability = true,
    showAsterisk = false,
  } = props
  const {errors} = form.formState || {}
  const fields = useSearchForAddressFields({form, label})
  const [deliveryAvailable, setDeliveryAvailable] = useState<boolean | null>(null)

  const {isExact: isRegisterPage} = useRouteMatch(['/account/register']) || {isExact: false}
  const {isExact: isBookASlotPage} = useRouteMatch(['/book-delivery']) || {isExact: false}

  const displayAvailability = showAvailability && deliveryAvailable !== null

  const applyAddressToForm = async (address: AddressDetails, searchText?: string) => {
    setEnterManually(true)

    form.clearErrors([
      'searchForAddress',
      'address1',
      'address2',
      'city',
      'stateCode',
      'postalCode',
      'addressId',
      'countryCode',
    ])

    form.setValue(fields.searchForAddress.name, address.PostalCode)
    form.setValue('address1', address.Line1 ?? searchText)
    form.setValue('address2', address.Line2 ?? '')
    form.setValue('city', address.City)
    form.setValue('stateCode', address.ProvinceName)
    form.setValue('postalCode', address.PostalCode)
    form.setValue('countryCode', address.CountryIso2.toUpperCase())

    if (isRegisterPage || isBookASlotPage) {
      form.setValue('addressId', address.Line1 ?? address.Line2 ?? searchText)
    }
  }

  const postcode = form.watch('postalCode')

  const getDeliveryAvailability = async (postcode: string) => {
    if (!postcode) return null

    try {
      const result: Response = await fetch(
        `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Delivery-GetDeliveryStatus?postcode=${encodeURIComponent(
          postcode,
        )}`,
        {
          credentials: 'include',
        },
      )
      const json: {data?: {code?: boolean}} = await result.json()

      if (json?.data && typeof json.data.code === 'boolean') {
        return json.data.code
      }

      return null
    } catch (e) {
      console.error(`Error occurred during Delivery-GetDeliveryStatus: ${postcode}`)
      return null
    }
  }

  useEffect(() => {
    ;(async function () {
      if (postcode &&
          postcode.length >= POSTCODE_MIN_LENGTH &&
          postcode.length <= POSTCODE_MAX_LENGTH) {
            const status = await getDeliveryAvailability(postcode)
            setDeliveryAvailable(status)
      }
    })()
  }, [postcode])

  return (
    <LoqateField
      field={{
        ...fields.searchForAddress,
        errorTextProps: {
          maxWidth: ['full', !isRegisterPage ? 'full' : 'half'],
          width: 'full',
        },
      }}
      setFoundedAddressToForm={applyAddressToForm}
      onSearchValueChange={(searchValue) => {
        form.setValue(fields.searchForAddress.name, searchValue)
        // Keeping postcode field in sync when changing `searchAddressValue`
        // when address part is expanded
        if (isEnterManually) {
          form.setValue('postalCode', searchValue)
        }
      }}
      inputGroupProps={inputGroupProps}
      showAsterisk={showAsterisk}
    >
      {displayAvailability && !errors[fields.searchForAddress.name] && (
        <DeliveryAvailability available={deliveryAvailable!} />
      )}
    </LoqateField>
  )
}

const DeliveryAvailability = ({available}: {available: boolean}) => {
  const {isExact: isRegisterPage} = useRouteMatch(['/account/register']) || {isExact: false}
  

  const {pathname} = useLocation()

  return (
    <Stack style={{marginTop: 7}}>
      {pathname !== '/checkout/summary' && (
        <Stack>
          <Flex gap="4px" align="center">
            {available ? (
              <CheckIcon color="accent1" boxSize="16px" />
            ) : (
              <CrossBoldIcon color="error" boxSize="12px" />
            )}

            <Text variant="text4" lineHeight="short">
              {available ? 'Online delivery available' : 'Online delivery unavailable'}
            </Text>
          </Flex>

          {!available && (
            <Text variant="text2" lineHeight="shorter" style={{color: '#bebebe'}}>
              We don&apos;t deliver to your area yet. Continue to register your interest and
              we&apos;ll be in touch if our service comes to your area.
            </Text>
          )}
        </Stack>
      )}
      {isRegisterPage && (
        <Stack>
          <Flex gap="4px" align="center">
            <CheckIcon color="accent1" boxSize="16px" />
            <Text variant="text4" lineHeight="short">
              Join or add a Bonus Card
            </Text>
          </Flex>

          <Text variant="text2" lineHeight="shorter" style={{color: '#bebebe'}}>
            Continue to register to get a Bonus Card.
          </Text>
        </Stack>
      )}
    </Stack>
  )
}
